import { useState, useRef } from "react";
import { API } from "@/lib/constants";
import { Image, Video, X } from "lucide-react";

const isVideo = (url) => {
  if (!url) return false;
  return url.includes("video") || url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov");
};

const resolveUrl = (url) => url?.startsWith('/') ? `${API.replace('/api','')}${url}` : url;

// Lightbox modal for full-screen media viewing
const MediaLightbox = ({ src, isVideoType, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        data-testid="media-lightbox-close"
      >
        <X size={24} />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="max-w-[95vw] max-h-[90vh]">
        {isVideoType ? (
          <video src={src} controls autoPlay className="max-w-full max-h-[90vh] rounded-lg" playsInline />
        ) : (
          <img src={src} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        )}
      </div>
    </div>
  );
};

// Horizontal scroll carousel used by both compact and full modes
const MediaCarousel = ({ media, aspectClass, onItemClick }) => {
  const scrollRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveIdx(idx);
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide rounded-xl"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {media.map((item, i) => {
          const url = typeof item === 'string' ? item : item?.url;
          if (!url) return null;
          const fullUrl = resolveUrl(url);
          const videoType = isVideo(url);
          return (
            <div key={i} className={`shrink-0 w-full snap-center ${aspectClass}`} onClick={() => onItemClick?.(fullUrl, videoType)}>
              {videoType ? (
                <div className="relative w-full h-full cursor-pointer group">
                  <video src={fullUrl} className="w-full h-full object-cover rounded-xl" muted playsInline preload="metadata" data-testid={`media-video-${i}`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-black/40 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
                      <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><polygon points="5,3 19,12 5,21" /></svg>
                    </div>
                  </div>
                </div>
              ) : (
                <img src={fullUrl} alt="" className="w-full h-full object-cover rounded-xl cursor-pointer" data-testid={`media-img-${i}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Dot indicators */}
      {media.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {media.map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeIdx ? 'bg-jeekota-primary' : 'bg-gray-300'}`} />
          ))}
        </div>
      )}
      {/* Media count badge */}
      {media.length > 1 && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md bg-black/60 text-white text-xs font-medium">{activeIdx + 1}/{media.length}</span>
        </div>
      )}
    </div>
  );
};

export const MediaDisplay = ({ media, compact = false }) => {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxIsVideo, setLightboxIsVideo] = useState(false);

  if (!media || media.length === 0) return null;

  const openLightbox = (fullUrl, videoType) => {
    setLightboxSrc(fullUrl);
    setLightboxIsVideo(videoType);
  };

  const closeLightbox = () => {
    setLightboxSrc(null);
    setLightboxIsVideo(false);
  };

  return (
    <>
      <div className="mt-3">
        <MediaCarousel
          media={media}
          aspectClass={compact ? "aspect-square" : "aspect-[4/3]"}
          onItemClick={openLightbox}
        />
      </div>
      {lightboxSrc && <MediaLightbox src={lightboxSrc} isVideoType={lightboxIsVideo} onClose={closeLightbox} />}
    </>
  );
};
